import { core_v1_DataSource_FieldSpec } from '@antimatterhq/asl-api-ts-v1';
import { WritableDraft } from 'immer';
import {
  DatasourceUpgradeDiff,
  DatasourceUpgradeFieldSpecDiff,
  DatasourceUpgradeFieldStatus,
  DatasourceUpgradeStepDiff,
} from '../datasourceUpgrade.types';
import {
  acceptField,
  acceptTemporaryField,
  rejectField,
  rejectTemporaryField,
  setFieldSpecText,
} from '../fieldSpec/datasourceUpgradeFieldSpecHelper';
import { setFiltersText } from '../fieldUtils/filters/datasourceUpgradeFiltersHelper';
import { setPreserveUnreferencedFieldsText } from '../fieldUtils/preserveUnreferencedFields/datasourceUpgradePreserveUnreferencedFieldsHelper';
import { Datasource } from '@/types/api';
import { filterNulls } from '@/utils/filterNulls';

export const getPreTransformChanges = (
  upgradeState: DatasourceUpgradeDiff | undefined,
) => {
  return filterNulls([
    upgradeState?.spec.silver.preTransform.filter,
    upgradeState?.spec.silver.preTransform.postFilter,
    ...(upgradeState?.spec.silver.preTransform.fields || []),
    upgradeState?.spec.silver.preTransform.utils?.unreferencedColumns?.preserve,
    upgradeState?.spec.silver.preTransform.utils?.unreferencedColumns
      ?.embedColumn,
    upgradeState?.spec.silver.preTransform.utils?.unreferencedColumns
      ?.embedColumnType,
    upgradeState?.spec.silver.preTransform.utils?.unreferencedColumns
      ?.omitColumns,
    upgradeState?.spec.silver.preTransform.utils?.unreferencedColumns
      ?.duplicatePrefix,
    ...(upgradeState?.spec.silver.preTransform.utils?.temporaryFields || []),
  ]).filter((change) => change.status !== 'unchanged');
};

export const setPreTransformText = (
  upgradeState: DatasourceUpgradeDiff | undefined,
) => {
  const preTransform = upgradeState?.spec.silver.preTransform;

  if (!preTransform) {
    return;
  }

  preTransform.fields?.forEach((field) => {
    setFieldSpecText(field);
  });

  setPreserveUnreferencedFieldsText(preTransform.utils);

  setFiltersText(preTransform);
};

export const queryPreTransformChanges = (
  preTransform: DatasourceUpgradeStepDiff,
  query: { status?: DatasourceUpgradeFieldStatus } = {},
) => {
  return preTransform.changes.filter(
    (q) => !query?.status || q.status === query.status,
  );
};

export const rejectTemporaryPreTransformField = (
  fieldDiff: DatasourceUpgradeFieldSpecDiff,
  datasource: WritableDraft<Datasource>,
) => {
  const table = datasource.spec.silver?.preTransform;

  if (!table) {
    throw new Error(
      'Cannot rejectTemporaryPreTransformField for no preTransform table',
    );
  }

  if (!table.utils?.temporaryFields) {
    table.utils = {
      ...table.utils,
      temporaryFields: [],
    };
  }

  rejectTemporaryField(fieldDiff, table.utils);
};

export const acceptTemporaryPreTransformField = (
  fieldDiff: DatasourceUpgradeFieldSpecDiff,
  datasource: WritableDraft<Datasource>,
) => {
  const table = datasource.spec.silver?.preTransform;

  if (!table) {
    throw new Error(
      'Cannot acceptTemporaryPreTransformField for no preTransform table',
    );
  }

  if (!table.utils?.temporaryFields) {
    table.utils = {
      ...table.utils,
      temporaryFields: [],
    };
  }

  acceptTemporaryField(fieldDiff, table.utils);
};

export const rejectPreTransformField = (
  fieldDiff: DatasourceUpgradeFieldSpecDiff,
  datasource: WritableDraft<Datasource>,
) => {
  const table = datasource.spec.silver?.preTransform;

  if (!table) {
    throw new Error('Cannot rejectPreTransformField for no preTransform table');
  }

  rejectField(
    (fields: string[]) =>
      (table.presetOverrides = {
        ...table.presetOverrides,
        omitFields: fields,
      }),
    (fields: core_v1_DataSource_FieldSpec[]) => (table.addFields = fields),
    table.presetOverrides?.omitFields || [],
    table.addFields || [],
    fieldDiff,
  );
};

export const acceptPreTransformField = (
  fieldDiff: DatasourceUpgradeFieldSpecDiff,
  datasource: WritableDraft<Datasource>,
) => {
  const table = datasource.spec.silver?.preTransform;

  if (!table) {
    throw new Error('Cannot acceptPreTransformField for no preTransform table');
  }

  acceptField(
    (fields: string[]) =>
      (table.presetOverrides = {
        ...table.presetOverrides,
        omitFields: fields,
      }),
    (fields: core_v1_DataSource_FieldSpec[]) => (table.addFields = fields),
    table.presetOverrides?.omitFields || [],
    table.addFields || [],
    fieldDiff,
  );
};
